import React, { useState, useRef, useEffect } from "react";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import {
	Avatar,
	Chip,
	CircularProgress,
	IconButton,
	InputBase,
	Typography,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
	phoneWrapper: {
		display: "flex",
		justifyContent: "center",
		width: "100%",
	},
	phoneFrame: {
		width: 340,
		maxWidth: "100%",
		height: 640,
		padding: 10,
		borderRadius: 44,
		backgroundColor: "#0F172A",
		boxShadow:
			theme.palette.type === "dark"
				? "0 24px 48px rgba(0,0,0,.55), 0 0 0 1px rgba(148,163,184,.18)"
				: "0 24px 48px rgba(15,23,42,.28)",
		flexShrink: 0,
		[theme.breakpoints.down("xs")]: {
			height: 560,
			borderRadius: 36,
		},
	},
	screen: {
		height: "100%",
		borderRadius: 34,
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		backgroundColor: theme.custom.chatBg,
		position: "relative",
		[theme.breakpoints.down("xs")]: {
			borderRadius: 28,
		},
	},
	notch: {
		position: "absolute",
		top: 0,
		left: "50%",
		transform: "translateX(-50%)",
		width: 110,
		height: 22,
		backgroundColor: "#0F172A",
		borderBottomLeftRadius: 14,
		borderBottomRightRadius: 14,
		zIndex: 5,
	},
	phoneHeader: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.25),
		padding: "30px 14px 12px",
		backgroundColor: theme.palette.primary.dark,
		color: "#fff",
		flexShrink: 0,
	},
	headerAvatar: {
		width: 38,
		height: 38,
		fontSize: "1rem",
		fontWeight: 700,
		backgroundColor: "rgba(255,255,255,.25)",
		color: "#fff",
	},
	headerInfo: {
		minWidth: 0,
		flex: 1,
	},
	headerName: {
		fontWeight: 700,
		fontSize: ".9rem",
		lineHeight: 1.2,
		color: "#fff",
	},
	headerStatus: {
		display: "flex",
		alignItems: "center",
		gap: 5,
		fontSize: ".68rem",
		opacity: 0.85,
	},
	onlineDot: {
		width: 7,
		height: 7,
		borderRadius: "50%",
		backgroundColor: theme.palette.primary.light,
		display: "inline-block",
	},
	messagesArea: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(1.5, 1.5, 1),
		display: "flex",
		flexDirection: "column",
		...theme.scrollbarStyles,
	},
	testChip: {
		alignSelf: "center",
		position: "sticky",
		top: 4,
		zIndex: 2,
		marginBottom: theme.spacing(1),
		fontSize: ".62rem",
		fontWeight: 700,
		letterSpacing: ".08em",
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(245,158,11,.22)"
				: "rgba(245,158,11,.16)",
		color: theme.palette.type === "dark" ? "#FCD34D" : "#B45309",
	},
	bubble: {
		maxWidth: "80%",
		padding: "6px 10px 4px",
		borderRadius: 10,
		marginBottom: theme.spacing(0.75),
		boxShadow: "0 1px 1px rgba(15,23,42,.10)",
		display: "flex",
		flexDirection: "column",
	},
	bubbleClient: {
		alignSelf: "flex-end",
		backgroundColor: theme.custom.bubbleOut,
		borderTopRightRadius: 2,
	},
	bubbleSystem: {
		alignSelf: "flex-start",
		backgroundColor: theme.custom.bubbleIn,
		borderTopLeftRadius: 2,
	},
	bubbleText: {
		fontSize: ".82rem",
		lineHeight: 1.4,
		color: theme.palette.text.primary,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
	},
	bubbleTime: {
		fontSize: ".62rem",
		color: theme.custom.textSecondary,
		alignSelf: "flex-end",
		marginTop: 2,
	},
	loadingWrapper: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(1),
	},
	emptyState: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		textAlign: "center",
		padding: theme.spacing(2),
		color: theme.custom.textSecondary,
	},
	emptyIcon: {
		fontSize: 42,
		marginBottom: theme.spacing(1),
		opacity: 0.5,
	},
	emptyTitle: {
		fontWeight: 600,
		fontSize: ".85rem",
		marginBottom: theme.spacing(0.5),
		color: "inherit",
	},
	emptyDescription: {
		fontSize: ".75rem",
		maxWidth: 220,
		color: "inherit",
	},
	inputBar: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 1.25),
		backgroundColor: theme.custom.surface,
		borderTop: `1px solid ${theme.custom.border}`,
		flexShrink: 0,
	},
	input: {
		flex: 1,
		backgroundColor: theme.custom.bubbleIn,
		borderRadius: 20,
		padding: "7px 14px",
		fontSize: ".85rem",
		border: `1px solid ${theme.custom.border}`,
	},
	sendButton: {
		width: 40,
		height: 40,
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		flexShrink: 0,
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
		"&.Mui-disabled": {
			backgroundColor: theme.custom.surfaceHover,
			color: theme.custom.textSecondary,
		},
	},
}));

// O caractere U+200E (marca de mensagem de bot) no início do corpo
// é removido apenas na exibição
const cleanMessageBody = (body) => (body || "").replace(/^‎+/, "");

const formatTime = (createdAt) => {
	if (!createdAt) return "";
	try {
		return format(parseISO(createdAt), "HH:mm");
	} catch (err) {
		return "";
	}
};

const PhoneFrame = ({
	connectionName,
	messages,
	loading,
	sending,
	disabled,
	onSendMessage,
}) => {
	const classes = useStyles();

	const [inputValue, setInputValue] = useState("");
	const messagesAreaRef = useRef();
	const lastCountRef = useRef(0);

	useEffect(() => {
		if (messages.length !== lastCountRef.current) {
			lastCountRef.current = messages.length;
			const area = messagesAreaRef.current;
			if (area) {
				area.scrollTop = area.scrollHeight;
			}
		}
	}, [messages]);

	const handleSend = async () => {
		const body = inputValue.trim();
		if (!body || sending || disabled) return;

		const sent = await onSendMessage(body);
		if (sent) {
			setInputValue("");
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const headerName = connectionName || i18n.t("testMode.phone.noConnection");
	const headerInitial = (connectionName || "?").charAt(0).toUpperCase();

	return (
		<div className={classes.phoneWrapper}>
			<div className={classes.phoneFrame}>
				<div className={classes.screen}>
					<div className={classes.notch} />
					<div className={classes.phoneHeader}>
						<Avatar className={classes.headerAvatar}>{headerInitial}</Avatar>
						<div className={classes.headerInfo}>
							<Typography className={classes.headerName} noWrap>
								{headerName}
							</Typography>
							{connectionName && (
								<span className={classes.headerStatus}>
									<span className={classes.onlineDot} />
									{i18n.t("testMode.phone.online")}
								</span>
							)}
						</div>
					</div>
					<div className={classes.messagesArea} ref={messagesAreaRef}>
						<Chip
							size="small"
							label={i18n.t("testMode.phone.chip")}
							className={classes.testChip}
						/>
						{loading && (
							<div className={classes.loadingWrapper}>
								<CircularProgress size={22} />
							</div>
						)}
						{!loading && messages.length === 0 && (
							<div className={classes.emptyState}>
								<ChatBubbleOutlineIcon className={classes.emptyIcon} />
								<Typography className={classes.emptyTitle}>
									{disabled
										? i18n.t("testMode.phone.emptyNoConnection")
										: i18n.t("testMode.phone.emptyTitle")}
								</Typography>
								<Typography className={classes.emptyDescription}>
									{disabled
										? i18n.t("testMode.phone.emptyNoConnectionHint")
										: i18n.t("testMode.phone.emptyDescription")}
								</Typography>
							</div>
						)}
						{messages.map((message) => (
							<div
								key={message.id}
								className={`${classes.bubble} ${
									message.fromMe ? classes.bubbleSystem : classes.bubbleClient
								}`}
							>
								<span className={classes.bubbleText}>
									{cleanMessageBody(message.body)}
								</span>
								<span className={classes.bubbleTime}>
									{formatTime(message.createdAt)}
								</span>
							</div>
						))}
					</div>
					<div className={classes.inputBar}>
						<InputBase
							className={classes.input}
							placeholder={i18n.t("testMode.phone.inputPlaceholder")}
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							onKeyPress={handleKeyPress}
							multiline
							rowsMax={4}
							disabled={disabled}
							inputProps={{
								"aria-label": i18n.t("testMode.phone.inputPlaceholder"),
							}}
						/>
						<IconButton
							className={classes.sendButton}
							onClick={handleSend}
							disabled={disabled || sending || !inputValue.trim()}
						>
							{sending ? (
								<CircularProgress size={20} color="inherit" />
							) : (
								<SendIcon fontSize="small" />
							)}
						</IconButton>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PhoneFrame;
